/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Jonathan Rowny
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * https://github.com/jrowny/react-absolute-grid
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash.sortby';

// Components
import BaseDisplayObject from './BaseDisplayObject';

// Helpers
import DragManager from './DragManager';
import LayoutManager from './LayoutManager';
import { cutoffs } from '../../../constants';

class DraggableGrid extends React.Component {
    constructor(props) {
        super(props);

        this.onResize = this.onResize.bind(this);
        this.getDOMWidth = this.getDOMWidth.bind(this);

        this.dragManager = new DragManager(
            props.onMove,
            props.onDragStart,
            props.onDragEnd,
            props.onDragMove,
            props.markerProp
        );

        this.state = {
            layoutWidth: 0,
        };
    }

    componentDidMount() {
        //If responsive, listen for resize
        if (this.props.responsive) {
            window.addEventListener('resize', this.onResize);
        }
        this.onResize();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    onResize() {
        if (window.requestAnimationFrame) {
            window.requestAnimationFrame(this.getDOMWidth);
        } else {
            setTimeout(this.getDOMWidth, cutoffs.RESIZE_TIMEOUT);
        }
    }

    getDOMWidth() {
        const width = this.container && this.container.clientWidth;
        if (this.state.layoutWidth !== width) {
            this.setState({ layoutWidth: width });
        }
    }

    render() {
        if (!this.state.layoutWidth || !this.props.items.length) {
            return (
                <div
                    ref={node => {
                        this.container = node;
                    }}
                />
            );
        }

        let filteredIndex = 0;
        const sortedIndex = {};

        /*
         If we actually sorted the array, React would re-render the DOM nodes
         Creating a sort index just tells us where each item should be
         This also clears out filtered items from the sort order and
         eliminates gaps and duplicate sorts
         */
        sortBy(this.props.items, this.props.sortProp).forEach(item => {
            const marker = item[this.props.markerProp];
            sortedIndex[marker] = filteredIndex;
            filteredIndex++;
        });

        const DisplayObject = this.props.displayObject;
        const itemsLength = this.props.items.length;
        const gridItems = this.props.items.map((item, i) => {
            const marker = item[this.props.markerProp];
            const index = sortedIndex[i];
            return (
                <BaseDisplayObject
                    className={this.props.cursorClass}
                    item={item}
                    index={index}
                    key={marker}
                    itemsLength={itemsLength}
                    animation={this.props.animation}
                    itemWidth={this.props.itemWidth}
                    itemHeight={this.props.itemHeight}
                    layoutWidth={this.state.layoutWidth}
                    verticalMargin={this.props.verticalMargin}
                    zoom={this.props.zoom}
                    markerProp={this.props.markerProp}
                    dragEnabled={this.props.dragEnabled}
                    dragManager={this.dragManager}
                >
                    <DisplayObject item={item} />
                </BaseDisplayObject>
            );
        });

        const options = {
            itemWidth: this.props.itemWidth,
            itemHeight: this.props.itemHeight,
            verticalMargin: this.props.verticalMargin,
            zoom: this.props.zoom,
        };
        const layout = new LayoutManager(options, this.state.layoutWidth);
        const gridStyle = {
            position: 'relative',
            display: 'block',
            height: layout.getTotalHeight(filteredIndex),
        };

        return (
            <div
                className={this.props.moving ? this.props.cursorClass : ''}
                style={gridStyle}
                ref={node => {
                    this.container = node;
                }}
            >
                {gridItems}
            </div>
        );
    }
}

export default DraggableGrid;

DraggableGrid.defaultProps = {
    animation: 'transform 300ms ease',
    dragEnabled: false,
    itemHeight: 128,
    items: [],
    itemWidth: 128,
    markerProp: 'marker',
    onDragEnd: () => {},
    onDragMove: () => {},
    onDragStart: () => {},
    onMove: () => {},
    responsive: false,
    sortProp: 'sort',
    verticalMargin: -1,
    zoom: 1,
};

DraggableGrid.propTypes = {
    animation: PropTypes.string,
    cursorClass: PropTypes.string,
    displayObject: PropTypes.func,
    dragEnabled: PropTypes.bool,
    itemHeight: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    itemWidth: PropTypes.number,
    markerProp: PropTypes.string,
    onDragEnd: PropTypes.func,
    onDragMove: PropTypes.func,
    onDragStart: PropTypes.func,
    onMove: PropTypes.func,
    moving: PropTypes.bool,
    responsive: PropTypes.bool,
    sortProp: PropTypes.string,
    verticalMargin: PropTypes.number,
    zoom: PropTypes.number,
};
